import { AppRegion, RegionCode, Regions } from './regions'
import { currencySign, DEFAULT_CURRENCY, getCurrencyDecimals } from './currency'

type RegionFormatData = Pick<AppRegion, 'currency' | 'languages' | 'code'>

type RegionOrConfig = RegionCode | RegionFormatData
const resolveRegionConfig = (
  region: RegionCode | RegionFormatData
): RegionFormatData => {
  if (typeof region === 'string') {
    return Regions[region]
  }
  return region
}

const getRegionLocale = (region: RegionOrConfig): string => {
  const language = resolveRegionConfig(region).languages.default
  if (!language) return 'en'
  return language
}

const getDateFormatter = (
  region: RegionOrConfig,
  options?: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  const locale = getRegionLocale(region)

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  })
}

const getPriceFormatter = (
  region: RegionOrConfig,
  options?: Intl.NumberFormatOptions
): Intl.NumberFormat => {
  const regionConfig = resolveRegionConfig(region)
  const currency = regionConfig?.currency ?? DEFAULT_CURRENCY
  const locale = getRegionLocale(region)
  const decimals = getCurrencyDecimals(currency)

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...options,
  })
}

const formatDate = (
  date: Date | string | number | null | undefined,
  region: RegionOrConfig,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return ''

  const value =
    typeof date === 'string' || typeof date === 'number' ? new Date(date) : date

  if (Number.isNaN(value.getTime())) {
    return ''
  }

  const formatter = getDateFormatter(region, options)

  return formatter.format(value)
}

const formatPrice = (
  price: string | number | null | undefined,
  region: RegionOrConfig,
  options?: Intl.NumberFormatOptions
): string => {
  if (price == null || price === '') return ''

  const numeric =
    typeof price === 'string' ? Number.parseFloat(price) : Number(price)

  if (!Number.isFinite(numeric)) return ''

  const formatter = getPriceFormatter(region, options)

  return formatter.format(numeric)
}

/**
 * Preferred format for compact UI-friendly price formatting:
 * - Drops decimals for whole numbers
 * - Uses the currency's decimals for non-whole numbers
 * - Uses our own currency sign/symbol mapping
 */
const formatPriceCompact = (
  price: string | number | null | undefined,
  region: RegionOrConfig
): string => {
  if (price == null || price === '') return ''
  const numeric =
    typeof price === 'string' ? Number.parseFloat(price) : Number(price)

  if (!Number.isFinite(numeric)) return ''

  // Normalize numeric based on whole vs decimal
  const isWhole = Number.isInteger(numeric)
  const regionConfig = resolveRegionConfig(region)
  const currency = regionConfig?.currency ?? DEFAULT_CURRENCY
  const symbol = currencySign[currency] ?? currency

  // Use full currency formatter to preserve locale-specific ordering
  // (whether symbol comes before/after, spacing, etc.), but override
  // the actual currency symbol with our own mapping.
  const locale = getRegionLocale(region)
  const baseFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: isWhole ? 0 : getCurrencyDecimals(currency),
    maximumFractionDigits: isWhole ? 0 : getCurrencyDecimals(currency),
  })

  const parts = baseFormatter.formatToParts(numeric)
  const replaced = parts
    .map((p) => (p.type === 'currency' ? { ...p, value: symbol } : p))
    .map((p) => p.value)
    .join('')

  return replaced.trim()
}

export const Format = {
  region: {
    // High-level formatting helpers
    date: formatDate,
    price: formatPrice,
    priceCompact: formatPriceCompact,
    // Low-level access to Intl formatters
    get: {
      date: getDateFormatter,
      price: getPriceFormatter,
    },
  },
}
